<template>
  <v-container fluid>
    <div class="dashboard-header">
      <h1 class="dashboard-title">สถิตินักเรียน/นักศึกษา ภาพรวม</h1>
      <p class="dashboard-subtitle">สำนักงานคณะกรรมการการอาชีพวศึกษา (สอศ.)</p>
    </div>
    <!-- Filter Section -->
    <div class="filter-section">
      <div class="filter-container">
        <div class="filter-group">
          <label class="filter-label">ปีการศึกษา</label>
          <select
            v-model="filters.rate_work_college_year"
            class="filter-input"
            @change="handleFilterChange"
          >
            <option value="">-- ทั้งหมด --</option>
            <option v-for="year in uniqueYears" :key="year" :value="year">
              {{ year }}
            </option>
          </select>
        </div>

        <div class="filter-group">
          <label class="filter-label">ชั้นปี</label>
          <select
            v-model="filters.rate_work_college_class"
            class="filter-input"
            @change="handleFilterChange"
          >
            <option value="">-- ทั้งหมด --</option>
            <option
              v-for="classItem in uniqueClasses"
              :key="classItem"
              :value="classItem"
            >
              {{ classItem }}
            </option>
          </select>
        </div>

        <button class="filter-btn-reset" @click="resetFilters">
          รีเซ็ต
        </button>
      </div>
    </div>

    <!-- Statistics Cards -->
    <div class="stats-cards">
      <div class="stat-card">
        <div class="stat-label">รวมนักเรียน</div>
        <div class="stat-value">{{ formatNumber(totalStudents) }}</div>
        <div class="stat-icon">👥</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">ปีการศึกษา</div>
        <div class="stat-value">{{ uniqueYears.length }}</div>
        <div class="stat-icon">📅</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">ชั้นปี</div>
        <div class="stat-value">{{ uniqueClasses.length }}</div>
        <div class="stat-icon">🎓</div>
      </div>
    </div>

    <!-- Charts Section -->
    <div class="charts-container">
      <div class="chart-box">
        <div class="chart-title">สถิตินักเรียนตามปีการศึกษา</div>
        <div v-if="!isLoadingChart" class="chart-wrapper">
          <div v-if="hasYearData" class="chart-inner">
            <apexchart
              v-if="chartByYearSeries && chartByYearSeries.length > 0"
              type="bar"
              :options="chartByYearOptions"
              :series="chartByYearSeries"
              height="400"
            />
          </div>
          <div v-else class="empty-chart">
            ไม่มีข้อมูล กรุณาตรวจสอบการเชื่อมต่อ API
          </div>
        </div>
        <div v-else class="loading-skeleton">กำลังโหลดข้อมูล...</div>
      </div>

      <div class="chart-box">
        <div class="chart-title">สถิตินักเรียนตามชั้นปี</div>
        <div v-if="!isLoadingChart" class="chart-wrapper">
          <div v-if="hasClassData" class="chart-inner">
            <apexchart
              v-if="chartByClassSeries && chartByClassSeries.length > 0"
              type="bar"
              :options="chartByClassOptions"
              :series="chartByClassSeries"
              height="400"
            />
          </div>
          <div v-else class="empty-chart">
            ไม่มีข้อมูล กรุณาตรวจสอบการเชื่อมต่อ API
          </div>
        </div>
        <div v-else class="loading-skeleton">กำลังโหลดข้อมูล...</div>
      </div>
    </div>

    <!-- Detail Table -->
    <div class="table-section">
      <div class="table-title">รายละเอียดข้อมูล</div>
      <div class="table-responsive">
        <table class="detail-table">
          <thead>
            <tr>
              <th>ปีการศึกษา</th>
              <th>ชั้นปี</th>
              <th>จำนวนนักเรียน</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in filteredData" :key="index">
              <td>{{ item.rate_work_college_year }}</td>
              <td>{{ item.rate_work_college_class }}</td>
              <td class="text-center">{{ formatNumber(item.count_std) }}</td>
            </tr>
            <tr v-if="filteredData.length === 0">
              <td colspan="3" class="text-center">ไม่พบข้อมูล</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </v-container>
</template>

<script>
export default {
  name: "vw_student_overall_count",
  data() {
    return {
      ApiKey: "HRvec2021",
      rawData: [],
      filteredData: [],
      isLoadingChart: true,
      filters: {
        rate_work_college_year: "",
        rate_work_college_class: ""
      }
    };
  },
  computed: {
    uniqueYears() {
      return [
        ...new Set(this.rawData.map(item => item.rate_work_college_year))
      ].sort();
    },
    uniqueClasses() {
      return [
        ...new Set(this.rawData.map(item => item.rate_work_college_class))
      ].sort();
    },
    totalStudents() {
      return this.filteredData.reduce(
        (sum, item) => sum + parseInt(item.count_std || 0),
        0
      );
    },
    groupedByYear() {
      const grouped = {};
      this.filteredData.forEach(item => {
        const year = item.rate_work_college_year;
        if (!grouped[year]) {
          grouped[year] = 0;
        }
        grouped[year] += parseInt(item.count_std || 0);
      });
      return grouped;
    },
    groupedByClass() {
      const grouped = {};
      this.filteredData.forEach(item => {
        const classItem = item.rate_work_college_class;
        if (!grouped[classItem]) {
          grouped[classItem] = 0;
        }
        grouped[classItem] += parseInt(item.count_std || 0);
      });
      return grouped;
    },
    hasYearData() {
      return Object.keys(this.groupedByYear).length > 0;
    },
    hasClassData() {
      return Object.keys(this.groupedByClass).length > 0;
    },
    chartByYearSeries() {
      try {
        const yearLabels = Object.keys(this.groupedByYear).sort();
        const yearData = yearLabels.map(year => this.groupedByYear[year]);

        if (yearData.length === 0) {
          return [];
        }

        return [
          {
            name: "จำนวนนักเรียน",
            data: yearData
          }
        ];
      } catch (error) {
        console.error("Error in chartByYearSeries:", error);
        return [];
      }
    },
    chartByYearOptions() {
      const yearLabels = Object.keys(this.groupedByYear).sort();

      return {
        chart: {
          type: "column",
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: true,
              zoom: true,
              zoomin: true,
              zoomout: true,
              pan: true,
              reset: true
            }
          },
          fontFamily: "'Roboto', sans-serif",
          animations: {
            enabled: true,
            speed: 800
          }
        },
        colors: ["#3B82F6"],
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "65%",
            borderRadius: 4,
            dataLabels: {
              position: "top"
            }
          }
        },
        dataLabels: {
          enabled: true,
          offsetY: -20,
          style: {
            fontSize: "13px",
            colors: ["#3B82F6"],
            fontWeight: "600",
            fontFamily: "'Roboto', sans-serif"
          },
          formatter: val => {
            return this.formatNumberShort(val);
          }
        },
        stroke: {
          show: false
        },
        grid: {
          borderColor: "#e5e7eb",
          strokeDashArray: 3
        },
        tooltip: {
          theme: "light",
          y: {
            formatter: val => {
              return `${this.formatNumber(val)} คน`;
            }
          }
        },
        xaxis: {
          categories: yearLabels,
          labels: {
            style: {
              fontSize: "12px",
              fontFamily: "'Roboto', sans-serif"
            },
            rotate: -45
          }
        },
        yaxis: {
          title: {
            text: "จำนวนนักเรียน (คน)",
            style: {
              fontSize: "12px",
              fontWeight: 600
            }
          },
          labels: {
            formatter: val => {
              return this.formatNumberShort(val);
            },
            style: {
              fontSize: "11px"
            }
          }
        }
      };
    },
    chartByClassSeries() {
      try {
        const classLabels = Object.keys(this.groupedByClass).sort();
        const classData = classLabels.map(cls => this.groupedByClass[cls]);

        if (classData.length === 0) {
          return [];
        }

        return [
          {
            name: "จำนวนนักเรียน",
            data: classData
          }
        ];
      } catch (error) {
        console.error("Error in chartByClassSeries:", error);
        return [];
      }
    },
    chartByClassOptions() {
      const classLabels = Object.keys(this.groupedByClass).sort();

      return {
        chart: {
          type: "bar",
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: true,
              zoom: true,
              zoomin: true,
              zoomout: true,
              pan: true,
              reset: true
            }
          },
          fontFamily: "'Roboto', sans-serif",
          animations: {
            enabled: true,
            speed: 800
          }
        },
        colors: ["#10B981"],
        plotOptions: {
          bar: {
            horizontal: true,
            barHeight: "70%",
            borderRadius: 4,
            dataLabels: {
              position: "right"
            }
          }
        },
        dataLabels: {
          enabled: true,
          offsetX: 10,
          style: {
            fontSize: "13px",
            colors: ["#10B981"],
            fontWeight: "600",
            fontFamily: "'Roboto', sans-serif"
          },
          formatter: val => {
            return this.formatNumberShort(val);
          }
        },
        stroke: {
          show: false
        },
        grid: {
          borderColor: "#e5e7eb",
          strokeDashArray: 3
        },
        tooltip: {
          theme: "light",
          y: {
            formatter: val => {
              return `${this.formatNumber(val)} คน`;
            }
          }
        },
        xaxis: {
          type: "numeric",
          labels: {
            formatter: val => {
              return this.formatNumberShort(val);
            },
            style: {
              fontSize: "11px"
            }
          }
        },
        yaxis: {
          categories: classLabels,
          labels: {
            style: {
              fontSize: "12px",
              fontFamily: "'Roboto', sans-serif"
            }
          }
        }
      };
    }
  },
  watch: {
    filteredData: {
      handler() {
        this.$forceUpdate();
      },
      deep: true
    }
  },
  async mounted() {
    await this.fetchStudentData();
  },
  methods: {
    formatNumber(num) {
      if (!num) return "0";
      return parseInt(num).toLocaleString("th-TH");
    },
    formatNumberShort(num) {
      if (!num) return "0";
      const n = parseInt(num);
      if (n >= 1000000) {
        return (n / 1000000).toFixed(1) + "M";
      } else if (n >= 1000) {
        return (n / 1000).toFixed(1) + "K";
      }
      return n.toLocaleString("th-TH");
    },
    async fetchStudentData() {
      try {
        this.isLoadingChart = true;

        if (!this.$http) {
          console.error("HTTP client not configured");
          this.isLoadingChart = false;
          return;
        }

        const response = await this.$http.post("vw_student_overall_count.php", {
          ApiKey: this.ApiKey
        });

        if (response.data && response.data.status) {
          this.rawData = response.data.data || [];
          this.applyFilters();
        } else {
          console.error("API error:", response.data);
        }

        this.isLoadingChart = false;
      } catch (error) {
        console.error("Error fetching student data:", error);
        this.isLoadingChart = false;
      }
    },
    applyFilters() {
      this.filteredData = this.rawData.filter(item => {
        const yearMatch =
          !this.filters.rate_work_college_year ||
          item.rate_work_college_year === this.filters.rate_work_college_year;
        const classMatch =
          !this.filters.rate_work_college_class ||
          item.rate_work_college_class === this.filters.rate_work_college_class;
        return yearMatch && classMatch;
      });
    },
    handleFilterChange() {
      this.applyFilters();
    },
    resetFilters() {
      this.filters.rate_work_college_year = "";
      this.filters.rate_work_college_class = "";
      this.applyFilters();
    }
  }
};
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}
.dashboard-header {
  margin-bottom: 32px;
  text-align: center;
}

.dashboard-title {
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 8px;
}

.dashboard-subtitle {
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
}

.student-overall-count-dashboard {
  padding: 24px;
  background: #f8f9fa;
  min-height: 100vh;
  font-family: "Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI",
    sans-serif;
}

/* Filter Section */
.filter-section {
  background: white;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.filter-container {
  display: flex;
  gap: 16px;
  align-items: flex-end;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 200px;
}

.filter-label {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.filter-input {
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  color: #1f2937;
  transition: all 0.2s ease;
  cursor: pointer;
}

.filter-input:hover {
  border-color: #d1d5db;
}

.filter-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.filter-btn-reset {
  padding: 10px 20px;
  background: #6b7280;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.filter-btn-reset:hover {
  background: #4b5563;
}

/* Statistics Cards */
.stats-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
}

.stat-card {
  background: white;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  position: relative;
  overflow: hidden;
}

.stat-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #3b82f6, #10b981);
}

.stat-label {
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
  margin-bottom: 12px;
}

.stat-value {
  font-size: 32px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 12px;
}

.stat-icon {
  font-size: 24px;
  position: absolute;
  top: 24px;
  right: 24px;
  opacity: 0.2;
}

/* Charts Section */
.charts-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
  gap: 24px;
  margin-bottom: 24px;
}

.chart-box {
  background: white;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  min-height: 550px;
}

.chart-title {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #e5e7eb;
}

.chart-wrapper {
  min-height: 420px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chart-inner {
  width: 100%;
}

.empty-chart {
  height: 420px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 14px;
  background: #f9fafb;
  border-radius: 6px;
}

.loading-skeleton {
  height: 420px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 14px;
  background: #f9fafb;
  border-radius: 6px;
}

/* Table Section */
.table-section {
  background: white;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.table-title {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #e5e7eb;
}

.table-responsive {
  overflow-x: auto;
}

.detail-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.detail-table thead th {
  background: #f3f4f6;
  padding: 12px;
  text-align: left;
  font-weight: 600;
  color: #374151;
  border-bottom: 2px solid #e5e7eb;
}

.detail-table tbody td {
  padding: 12px;
  border-bottom: 1px solid #e5e7eb;
  color: #1f2937;
}

.detail-table tbody tr:hover {
  background: #f9fafb;
}

.text-center {
  text-align: center;
}

/* Responsive */
@media (max-width: 768px) {
  .student-overall-count-dashboard {
    padding: 16px;
  }

  .filter-container {
    flex-direction: column;
  }

  .filter-group {
    width: 100%;
    min-width: auto;
  }

  .filter-btn-reset {
    width: 100%;
  }

  .charts-container {
    grid-template-columns: 1fr;
  }

  .stats-cards {
    grid-template-columns: 1fr;
  }

  .detail-table {
    font-size: 12px;
  }

  .detail-table thead th,
  .detail-table tbody td {
    padding: 8px;
  }

  .chart-box {
    min-height: auto;
  }
}
</style>
